/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  IconButton,
  Popper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import CloseIcon from '@mui/icons-material/Close';
import { MenuContainer } from './menu-items';
import {
  AppDispatch,
  RootState,
  useAppSelector,
} from 'packages/blu-booking/src/store';
import { bluBookingLoginData } from 'packages/blu-booking/src/store/root';
import { usePointOfSalesFiltered } from 'packages/blu-booking/src/data_fetching/PointOfSale/PointOfSalesFilter';
import {
  setLockPointOfSale,
  setShopSignIsRecallOn,
} from '../../../../store/AppointmentForm';

let allFilters = true;

export const PointOfSale = memo(() => {
  // ! VARIABLES
  const { setValue, reset, trigger } = useFormContext();
  const serviceId = useWatch({ name: 'serviceId' });
  const userId = useWatch({ name: 'userId' });
  // const studioRequired = useWatch({ name: "studioRequired" });
  const pos = useWatch({ name: 'pointOfSaleId' });
  const [maxHeight, setMaxHeight] = useState<number | 'auto'>('auto');

  const { pointOfSales } = useSelector(
    (state: RootState) => state.bluBookingCalendarFilters
  );
  const { lockService, lockUser } = useSelector(
    (state: RootState) => state.bluBookingAppointmentForm
  );

  // ? Popper anchor determines position
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // ? Value of text field
  const [textfieldValue, setTextFieldValue] = useState<string>('');

  // ? Prevent popper to close on Other button click
  const [clickWithoutClose, setClickWithoutClose] = useState<boolean>(false);

  // ? Ref of text field
  const inputRef = useRef<HTMLElement>(null);

  const dispatch = useDispatch<AppDispatch>();

  const { isManualAppointmentBlock, pointOfSaleId } =
    useAppSelector(bluBookingLoginData);

  // ? Call for GET point of sale associated
  const { data: result } = usePointOfSalesFiltered({
    lockService,
    lockUser,
    serviceId,
    userId,
  });

  const data = useDeferredValue(result);
  const [keyDown, setKeyDown] = useState<string | undefined>();
  // ! END

  // ! FUNCTIONS
  useEffect(() => {
    if (pointOfSales !== undefined) {
      if (pos in pointOfSales) {
        setTextFieldValue(pointOfSales[pos]?.branch);
        dispatch(setShopSignIsRecallOn(pointOfSales[pos]?.shopSignIsRecallOn));
      }
      if (pos === '') setTextFieldValue('');
    }
  }, [dispatch, pointOfSales, pos]);

  useEffect(() => {
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom; // Spazio sotto il campo
      const spaceAbove = rect.top; // Spazio sopra il campo
      const availableSpace = Math.max(spaceBelow, spaceAbove); // Usa lo spazio maggiore

      setMaxHeight(availableSpace - 10); // Imposta il maxHeight (con un limite massimo di 300px)
    }
  }, [anchorEl]);

  // ? Handle change on text field
  const handleOnChange = useCallback((event: any) => {
    if (allFilters) allFilters = false;
    setTextFieldValue(event.target.value);
  }, []);

  // ? handle open popper on focus input
  const handleClick = useCallback((): void => {
    setAnchorEl(inputRef?.current);
  }, []);

  // ? handle close popper
  const handleClose = useCallback((): void => {
    console.log('handleClose');
    if (textfieldValue !== '') {
      const set = Object.values(pointOfSales).find(
        (p) => p.branch === textfieldValue
      );
      if (set === undefined) setTextFieldValue(pointOfSales[pos].branch);
    }
    allFilters = true;
    setAnchorEl(null);
  }, [pointOfSales, pos, textfieldValue]);

  // ? handle blur on input
  const manageOnBlur = useCallback(() => {
    console.log('manageOnBlur');

    if (textfieldValue === '') {
      setValue('pointOfSaleId', '');
      trigger();
    }
    if (!clickWithoutClose) handleClose();
    else setClickWithoutClose(false);
    // else otherCLicked = false;
  }, [clickWithoutClose, handleClose, setValue, textfieldValue, trigger]);

  // ? erase input value
  const handleErase = useCallback(() => {
    setClickWithoutClose(true);
    setTextFieldValue('');
    setValue('pointOfSaleId', '');
    trigger();
  }, [setValue, trigger]);

  // ? Handle click on MenuItem
  const handleItemCLick = useCallback(
    (id: string, branch: string, lock = false) => {
      if (id === null) {
        handleClose();
        reset({
          pointOfSaleId: '',
          studioId: '',
        });
        trigger();
        return;
      }
      setValue('pointOfSaleId', id);
      setTextFieldValue(branch);
      dispatch(setLockPointOfSale(lock));
      setValue('cercaDisponibilitaUsed', false);

      setValue('studioId', '');
      if (isManualAppointmentBlock) setValue('userId', '');
      trigger();
      // }
      // handleClose();

      return;
    },
    [dispatch, handleClose, isManualAppointmentBlock, reset, setValue, trigger]
  );

  // ? get options
  const getOptions = useCallback(() => {
    const temp = JSON.parse(JSON.stringify(pointOfSales));
    let result = {
      association: [],
      unassociation: [],
    };
    if (isManualAppointmentBlock) {
      if (data) {
        if (data.length)
          return {
            association: data,
            unassociation: [],
          };
      }
      return result;
    }
    if (data !== undefined) {
      if (data.length === 0)
        return {
          ...result,
          unassociation: Object.values(pointOfSales),
        };
      else {
        for (const pointOfSale of data) delete temp[pointOfSale.id];
        result = {
          association: data as any,
          unassociation: Object.values(temp),
        };
      }
    }
    if (!allFilters)
      return {
        association: result.association.filter((value: any) => {
          const reg = new RegExp(textfieldValue, 'i');
          return reg.test(value.branch);
        }),
        unassociation: result.unassociation.filter((value) => {
          const reg = new RegExp(textfieldValue, 'i');
          return reg.test((value as any).branch);
        }),
      };
    return result;
  }, [data, isManualAppointmentBlock, pointOfSales, textfieldValue]);

  // ? Erase icon
  const Erase = useMemo(() => {
    if (open)
      return (
        <IconButton
          sx={{ width: '24px', height: '24px', p: 0, m: 0 }}
          onMouseDown={handleErase}
          onClick={() => inputRef.current?.focus()}
        >
          <CloseIcon sx={{ fontSize: '20px' }} />
        </IconButton>
      );
    return null;
  }, [handleErase, open]);

  // ? Arrow open and close
  const Arrow = useMemo(() => {
    if (open)
      return (
        <IconButton
          sx={{ width: '24px', height: '24px', p: 0, m: 0 }}
          onClick={handleClose}
        >
          <ArrowDropUpIcon sx={{ cursor: 'pointer' }} />
        </IconButton>
      );
    return (
      <IconButton
        sx={{ width: '24px', height: '24px', p: 0, m: 0 }}
        onClick={(event) => {
          handleClick();
          inputRef.current?.focus();
        }}
      >
        <ArrowDropDownIcon />
      </IconButton>
    );
  }, [handleClick, handleClose, open]);

  const handleColor = useCallback(() => {
    if (pointOfSaleId !== pos) return 'var(--alert-color)';
    return;
  }, [pointOfSaleId, pos]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (
      event.key === 'Enter' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowDown'
    ) {
      if (!open) {
        handleClick();
        return;
      }
    }
    if (event.key === 'Escape') {
      handleClose();
    }
    if (
      event.key !== 'ArrowUp' &&
      event.key !== 'ArrowDown' &&
      event.key !== 'Enter'
    )
      return;
    setKeyDown(event.key);
  };
  // ! END

  return (
    <Box style={{ width: '49%' }} onKeyDown={handleKeyDown}>
      <Typography variant="subtitle2" sx={{ pt: '3px' }}>
        Punto vendita
      </Typography>
      <TextField
        inputRef={inputRef}
        id="point_of_sale_selector"
        onBlur={manageOnBlur}
        onFocus={handleClick}
        value={textfieldValue}
        placeholder="Seleziona un punto vendita"
        sx={{
          color: handleColor(),
          '& .MuiInput-input': {
            color: handleColor(),
          },
        }}
        InputProps={{
          autoComplete: 'off',
          startAdornment: (
            <Stack direction="row" pr={1} justifyItems="center"></Stack>
          ),
          endAdornment: (
            <>
              {Erase}
              {Arrow}
            </>
          ),
        }}
        onChange={handleOnChange}
        fullWidth
      />
      <Popper
        open={open}
        style={{ zIndex: 1301, width: 'fit-content' }}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[
          {
            name: 'preventOverflow',
            options: {
              boundary: 'window', // Impedisce che il popper esca dalla finestra
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['top-start', 'bottom-start'], // Permette di invertire la posizione se necessario
            },
          },
        ]}
      >
        {/* <Box
          sx={{
            maxHeight: maxHeight !== 'auto' ? `${maxHeight}px` : maxHeight, // Altezza dinamica
            overflowY: 'auto', // Abilita lo scroll se necessario
            background: 'white',
          }}
        > */}
        <MenuContainer
          pointOfSaleId={pos}
          keyDown={keyDown}
          setKeyDown={setKeyDown}
          handleClose={handleClose}
          options={getOptions()}
          textfieldValue={textfieldValue}
          width={inputRef?.current?.clientWidth}
          focus={() => inputRef?.current?.focus()}
          handleClick={(id: string, name: string, lock?: boolean) =>
            handleItemCLick(id, name, lock)
          }
          setClickWithoutClose={(v: boolean) => setClickWithoutClose(v)}
          maxHeight={maxHeight}
        />
        {/* </Box> */}
      </Popper>
    </Box>
  );
});
